import React, { useContext, useMemo } from 'react';

import { isNil } from 'lodash';
import { useLocation } from 'react-router-dom';
import { GenericSideBar } from 'src/components/SideBar/GenericSideBar';
import { getMatch } from 'src/components/SideBar/helpers';
import { PageStructure } from 'src/components/SideBar/sidebar-types';
import { ROUTES } from 'src/constants/routes';
import { AppContext } from 'src/contexts/appContext';
import aiCopilotSideBarTokens from 'src/icons/sidebar/ai-copilot';
import analyticsSideBarTokens from 'src/icons/sidebar/analytics';
import documentsSideBarTokens from 'src/icons/sidebar/documents';
import financialsSideBarTokens from 'src/icons/sidebar/financials';
import operationsSideBarTokens from 'src/icons/sidebar/operations';
import royaltiesOperationsSideBarTokens from 'src/icons/sidebar/royalties-operations';
import { reTitle } from 'src/icons/sidebar/utils';
import vendorsSideBarTokens from 'src/icons/sidebar/vendors';
import { isOnGammaProd } from 'src/util/config';

import './SideBar.scss';

const primaryPageStructure: PageStructure = [
    {
        tokens: aiCopilotSideBarTokens,
        name: 'AI Copilot',
        href: ROUTES.AI.INDEX
    },
    {
        tokens: documentsSideBarTokens,
        name: 'Documents',
        href: ROUTES.AGREEMENTS.INDEX
    },
    {
        tokens: vendorsSideBarTokens,
        name: 'Vendors',
        href: ROUTES.VENDORS.INDEX
    },
    {
        tokens: financialsSideBarTokens,
        name: 'Financials'
    },
    {
        tokens: analyticsSideBarTokens,
        name: 'Analytics',
        children: [
            {
                name: 'AMU Metrics',
                href: ROUTES.ANALYTICS.LICENSING_KPI.AMU_METRICS
            },
            {
                name: 'Prime Metrics',
                href: ROUTES.ANALYTICS.LICENSING_KPI.PRIME_METRICS
            },
            {
                name: 'AMF Metrics',
                href: ROUTES.ANALYTICS.LICENSING_KPI.AMF_METRICS
            },
            {
                name: 'Month Over Month Payments',
                href: ROUTES.ANALYTICS.MONTH_OVER_MONTH_PAYMENTS
            }
        ]
    },
    {
        tokens: royaltiesOperationsSideBarTokens,
        name: 'Royalties Operations',
        children: [
            {
                name: 'Royalty Manager',
                href: ROUTES.ROYALTY_MANAGER.INDEX
            },
            {
                name: 'Ratecards',
                href: ROUTES.RATE_CARDS.INDEX
            },
            {
                name: 'Ratecard Association',
                href: ROUTES.DEALS.INDEX
            },
            {
                name: 'PAPER Plans',
                href: ROUTES.PAPER_PLANS.INDEX
            },
            {
                name: 'PAPER Plan Mappings',
                href: ROUTES.PAPER_PLAN_MAPPINGS.INDEX
            },
            {
                name: 'Base Plan Launch Dates',
                href: ROUTES.BASE_PLAN_LAUNCH_DATES.INDEX
            },
            {
                name: 'PAPER Plan Weights',
                href: ROUTES.PLAN_WEIGHTS.INDEX
            },
            {
                name: 'Bundle pricing details',
                href: ROUTES.BUNDLE_PRICING.INDEX
            }
        ]
    },
    {
        tokens: operationsSideBarTokens,
        name: 'Operations',
        children: [
            {
                name: 'Service Launch Dates',
                href: ROUTES.SERVICE_LAUNCHES.INDEX
            },
            {
                name: 'Territory Settings',
                href: ROUTES.CURRENCY_CONTENT_ACQUISITION_ENTITY.INDEX
            },
            {
                name: 'Reporting',
                href: ROUTES.REPORTING.INDEX
            },
            {
                name: 'Compare Ratecards',
                href: ROUTES.COMPARE.CONTRACTS
            },
            {
                name: 'Compare Ratecard Associations',
                href: ROUTES.COMPARE.DEALS
            },
            {
                name: 'PAPER Plan review',
                href: ROUTES.REVIEW_PAPER_PLANS.INDEX
            }
        ]
    }
];

const settingsPageStructure: PageStructure = [
    {
        tokens: reTitle(operationsSideBarTokens, 'General Settings'),
        name: 'General Settings',
        href: ROUTES.SETTINGS.INDEX
    },
    {
        tokens: reTitle(aiCopilotSideBarTokens, 'AI Settings'),
        name: 'AI Settings',
        href: ROUTES.SETTINGS.AI
    }
];

export const SideBar2: React.FC = () => {
    const location = useLocation();
    const appContext = useContext(AppContext);

    // use this for context-based feature gating
    const hiddenLinks = useMemo(() => {
        const links = new Set<string>(['Financials']);
        if (!appContext.royaltyManagerFeatureEnabled) {
            links.add('Royalty Manager');
        }
        if (isOnGammaProd()) {
            links.add('PAPER Plan review');
        }
        return links;
    }, [appContext.royaltyManagerFeatureEnabled]);

    const match = useMemo(() => {
        return getMatch(location.pathname, [primaryPageStructure, settingsPageStructure]);
    }, [location.pathname]);

    if (location.pathname === '/' || location.pathname === '') {
        return (
            <GenericSideBar
                identifier={'primary'}
                pageStructure={primaryPageStructure}
                selected={match}
                hiddenLinks={hiddenLinks}
                isHome={true}
            />
        );
    } else if (isNil(match)) {
        return <></>;
    } else if (match.startsWith(ROUTES.SETTINGS.INDEX)) {
        return (
            <GenericSideBar
                identifier={'settings'}
                pageStructure={settingsPageStructure}
                hiddenLinks={hiddenLinks}
                selected={match}
            />
        );
    } else {
        return (
            <GenericSideBar
                identifier={'primary'}
                pageStructure={primaryPageStructure}
                hiddenLinks={hiddenLinks}
                selected={match}
            />
        );
    }
};
